import { useEffect, useState } from "react"
import { useDebounceValue } from "usehooks-ts"

import {
  CONTAINS_OPTION,
  DOES_NOT_CONTAIN_OPTION,
  EXISTS_OPTION,
  IS_NOT_OPTION,
  IS_OPTION,
  NOT_EXISTS_OPTION,
  TYPE_OPTION_NAME_MAP,
} from "./data"

export function typeExistsOrNot(config) {
  return (
    [EXISTS_OPTION.value, NOT_EXISTS_OPTION.value].indexOf(config.type) !== -1
  )
}

export function broadcastPropertyChange(
  updatedProperty,
  fieldConfig,
  isConfigCompleteFunction,
  onPropertyChanged,
  onConfigCompleteChanged
) {
  const newConfig = {
    ...fieldConfig,
    ...{ [updatedProperty.name]: updatedProperty.value },
  }
  onPropertyChanged(updatedProperty)
  onConfigCompleteChanged(isConfigCompleteFunction(newConfig), newConfig)
}

export function isSingleSelection(config) {
  return (
    [
      IS_OPTION.value,
      IS_NOT_OPTION.value,
      CONTAINS_OPTION.value,
      DOES_NOT_CONTAIN_OPTION.value,
    ].indexOf(config.type) !== -1
  )
}

export function isConfigCompleteAllOptions(newConfig) {
  if (typeExistsOrNot(newConfig)) {
    return true
  }
  return !!newConfig.type && !!newConfig.value && newConfig.value.length > 0
}

export function isConfigComplete4Options(newConfig) {
  if (isSingleSelection(newConfig)) {
    return !!newConfig.type && !!newConfig.value
  } else {
    return !!newConfig.type && !!newConfig.value && newConfig.value.length > 0
  }
}

export function useHandleAutocompleteInputValue(
  fieldConfigProperty,
  optionNameToLabelMap
) {
  const [inputValue, setInputValue] = useState("")

  useEffect(() => {
    if (fieldConfigProperty) {
      setInputValue(
        optionNameToLabelMap[fieldConfigProperty] ||
          TYPE_OPTION_NAME_MAP[fieldConfigProperty] ||
          ""
      )
    }
    // FilterRow passes a new field-name map each render; depending on it
    // would reset the input while the user is typing a field name.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldConfigProperty])

  function onInputChange(_, newValue) {
    setInputValue(newValue)
  }

  function isOptionEqualToValue(option, value) {
    if (typeof value === "string") {
      return option.fieldName === value
    }
    return option.id === value.id
  }

  function getOptionLabel(option) {
    if (typeof option === "string") {
      return optionNameToLabelMap[option]
    }
    return option.label
  }

  return {
    inputValue,
    onInputChange,
    isOptionEqualToValue,
    getOptionLabel,
  }
}

export function useBroadcastDebouncedValue({
  value,
  fieldConfig,
  isConfigComplete,
  onPropertyChanged,
  onConfigCompleteChanged,
}) {
  const debouncedValue = useDebounceValue(value)[0]

  useEffect(
    () => {
      broadcastPropertyChange(
        { name: "value", value: debouncedValue },
        fieldConfig,
        isConfigComplete,
        onPropertyChanged,
        onConfigCompleteChanged
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedValue]
  )
}

export function useFiltersState(processFilters = (filters) => filters) {
  const [filters, setFilters] = useState([])
  const [mapFilter, setMapFilter] = useState(null)

  function combineFilters() {
    let combinedFilters = processFilters(filters.slice())
    if (mapFilter) {
      combinedFilters = combinedFilters.concat(mapFilter)
    }
    return combinedFilters
  }

  return {
    filters,
    setFilters,
    mapFilter,
    setMapFilter,
    combineFilters,
  }
}

export function useDialogState() {
  const [filters, setFilters] = useState([])
  const debouncedFilters = useDebounceValue(filters)[0]
  const [isFiltersDialogOpen, setFiltersDialogOpen] = useState(false)

  function openFiltersDialog() {
    setFiltersDialogOpen(true)
  }

  function closeFiltersDialog() {
    setFiltersDialogOpen(false)
  }

  function filtersReadyForApply(newFilters) {
    return newFilters.every((filter) => !filter.field || filter._complete)
  }

  function handleFiltersUpdated(newFilters) {
    if (!filtersReadyForApply(newFilters)) {
      return
    }
    setFilters(newFilters.filter((filter) => filter.field && filter._complete))
  }

  function clearFilters() {
    setFilters([])
  }

  return {
    filters,
    setFilters,
    debouncedFilters,
    isFiltersDialogOpen,
    openFiltersDialog,
    closeFiltersDialog,
    handleFiltersUpdated,
    clearFilters,
  }
}

export function getFieldNameMap(filterFields) {
  return Object.fromEntries(
    filterFields.map((field) => [field.fieldName, field.label])
  )
}

export function transformRealityFilters(filters) {
  return filters.map((filter) =>
    filter.field === "mls_id" ? { ...filter, value: filter.value.id } : filter
  )
}
